/**
 * Auth Context
 *
 * Manages authentication state across the entire app.
 * Wrap your app in <AuthProvider> and use useAuth() anywhere.
 *
 * What it provides:
 *   user         — current user object (null if logged out)
 *   token        — JWT token string
 *   loading      — true while checking if user is still logged in
 *   isGuest      — true when this is an auto-created guest session
 *   login()      — call with email+password, stores token
 *   logout()     — clears token, redirects to login
 *   signup()     — create account
 *   claimAccount() — turn the current guest session into a real account
 *   startGuestSession() — provision a guest so a first-time visitor can walk
 *                  straight into the app (called by ProtectedRoute)
 *
 * Staying signed in:
 *   The token and user are kept in storage (see services/storage.js —
 *   localStorage on web, Keychain-backed Preferences on iOS), so closing the
 *   tab — or the whole app — doesn't sign anyone out. Tokens expire after 7
 *   days, so on
 *   every app load the saved one is exchanged for a fresh one; someone who
 *   opens JAI at least once a week is never asked to log in again. If that
 *   exchange fails the session really is dead, and only then does a registered
 *   user get sent back to the login page.
 */

import { createContext, useContext, useState, useEffect } from 'react'
import { authApi, billingApi, referralsApi, authRedirect } from '../services/api'
import { readSync, writeSync, clearSync } from '../services/storage'
import { trackEvent, identifyUser, resetUser } from '../analytics'
import { hapticSignInSuccess } from '../services/haptics'
import { teardownPushNotifications } from '../services/pushNotifications'

const AuthContext = createContext(null)

/**
 * In-flight guest-session request, shared across every caller.
 *
 * Provisioning a guest creates a real row in the database, so it must happen
 * exactly once no matter how many times it's asked for. Two things make that
 * non-trivial: React runs effects twice in development StrictMode, and a
 * couple of components can ask on the same render. Holding the promise at
 * module level (not in state) means the second caller waits on the first
 * request instead of starting a second one.
 */
let guestSessionInFlight = null

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [token, setToken] = useState(null)
  const [loading, setLoading] = useState(true)

  // The user's subscription tier ('starter' | 'pro' | 'elite'). Not part of
  // the signup/login response (that's set later by the Stripe webhook), so
  // it's fetched separately via /api/billing/status and kept here so any
  // component can read it with useAuth(). Defaults to 'starter' — anyone
  // without an active paid subscription gets Starter access, same rule the
  // backend uses (see backend/src/config/planAccess.js resolvePlan()).
  const [plan, setPlan] = useState('starter')

  // Which billing cycle the user's paid plan renews on: 'monthly' | 'annual',
  // or null on Starter / no subscription. The pricing page needs this to tell
  // "already on Pro monthly" apart from "already on Pro annual" — the first
  // should still be offered the switch to yearly billing, the second shouldn't
  // be offered anything on the Pro card at all.
  const [planInterval, setPlanInterval] = useState(null)

  // A downgrade queued for the next renewal: { plan, interval, effectiveAt } or
  // null. `plan` above stays the CURRENT plan until the switch really happens.
  const [scheduledChange, setScheduledChange] = useState(null)

  // True when a REGISTERED account's saved session turned out to be dead on
  // startup. It's the one case that must not be met with a guest account:
  // ProtectedRoute normally hands a session-less visitor a guest login, which
  // for someone who simply hasn't opened JAI in over a week would quietly
  // swap them out of their own account — leagues, notes and subscription
  // apparently gone. This flag routes them to the login page instead.
  const [sessionExpired, setSessionExpired] = useState(false)
  // True until the first refreshPlan() call resolves. PlanGate (see
  // components/PlanGate.jsx) waits for this before deciding whether to
  // redirect someone away from a tool — otherwise a Pro/Elite user would
  // flash-redirect for a moment on every page load while 'starter' is still
  // just the default and the real plan hasn't loaded yet.
  const [planLoading, setPlanLoading] = useState(true)

  // League downgrade/selection flow — true when a plan downgrade left the
  // user with more active leagues than their new plan allows. While true,
  // ProtectedRoute (see components/ProtectedRoute.jsx) blocks normal
  // dashboard/tool access and sends them to /select-leagues instead. Comes
  // from the same /api/billing/status call as `plan`.
  const [needsLeagueSelection, setNeedsLeagueSelection] = useState(false)

  // "Founding Manager" perks earned through the waitlist referral program
  // (config/referralProgram.js), matched to this account by email:
  //   referralUnlocks — tool route slugs unlocked early (e.g. ['trash-talk']),
  //                     checked by HiddenRoute alongside the plan bypass
  //   foundingManager — true once the badge tier was reached (shown in the
  //                     account menu)
  // Display + routing convenience only — the backend re-checks the same rule
  // on every tool API call (requireTool in planAccessMiddleware.js).
  const [referralUnlocks, setReferralUnlocks] = useState([])
  const [foundingManager, setFoundingManager] = useState(false)

  const refreshPlan = async () => {
    try {
      // Both fetches feed the same routing decision (can this user open this
      // tool?), so they run together and planLoading only clears when both
      // have answered — otherwise HiddenRoute could bounce someone off a
      // referral-unlocked tool in the moment before their perks arrive.
      const [planRes, referralRes] = await Promise.allSettled([
        billingApi.getStatus(),
        referralsApi.getMine(),
      ])

      if (planRes.status === 'fulfilled') {
        const res = planRes.value
        const validPlans = ['starter', 'pro', 'elite']
        const active = res.data?.active && validPlans.includes(res.data?.plan)
        const resolved = active ? res.data.plan : 'starter'
        setPlan(resolved)
        // The API answers in Stripe's vocabulary ('month'/'year'); the app
        // talks in 'monthly'/'annual' everywhere else, so translate once here
        // rather than at each place that reads it.
        setPlanInterval(active ? (res.data.interval === 'year' ? 'annual' : res.data.interval === 'month' ? 'monthly' : null) : null)
        setNeedsLeagueSelection(!!res.data?.needsLeagueSelection)
        setScheduledChange(active ? (res.data?.scheduledChange || null) : null)
      } else {
        // If this fails (logged out, network hiccup, etc.) fall back to the
        // safe default rather than leaving a stale/incorrect plan showing.
        setPlan('starter')
        setPlanInterval(null)
        setScheduledChange(null)
        setNeedsLeagueSelection(false)
      }

      if (referralRes.status === 'fulfilled') {
        setReferralUnlocks(referralRes.value.data?.unlockedTools ?? [])
        setFoundingManager(!!referralRes.value.data?.foundingManager)
      } else {
        setReferralUnlocks([])
        setFoundingManager(false)
      }
    } finally {
      setPlanLoading(false)
    }
  }

  /** Save a { token, user } pair everywhere it needs to live. */
  const storeSession = (newToken, newUser) => {
    writeSync('jai_token', newToken)
    writeSync('jai_user', JSON.stringify(newUser))
    setToken(newToken)
    setUser(newUser)
    // Whatever went stale before, there's a live session now — otherwise
    // ProtectedRoute would keep bouncing a freshly signed-in user to /login.
    setSessionExpired(false)
  }

  const clearSession = () => {
    clearSync('jai_token')
    clearSync('jai_user')
    setToken(null)
    setUser(null)
  }

  /**
   * Create a guest account and sign into it.
   *
   * Called by ProtectedRoute when someone with no session opens an app screen
   * — that's what lets a first-time visitor see the product instead of a
   * sign-up form. Deliberately NOT called on app load: someone reading the
   * privacy policy or heading for the login page shouldn't have an account
   * created for them.
   *
   * Returns the new user, or null if it couldn't be created (the server caps
   * how many guest sessions one network can start — see guestSessionLimiter).
   * Callers must handle null by sending the visitor to sign up rather than
   * leaving them on a blank screen.
   */
  const startGuestSession = async () => {
    if (guestSessionInFlight) return guestSessionInFlight

    guestSessionInFlight = (async () => {
      try {
        const { data } = await authApi.createGuest()
        storeSession(data.token, data.user)
        // Funnel start. Without this a new guest was invisible to analytics
        // until their first pageview, and never tagged is_guest before then.
        identifyUser(data.user)
        trackEvent('guest_started')
        setPlan('starter')
        setPlanInterval(null)
        setNeedsLeagueSelection(false)
        refreshPlan()
        // Deliberately NOT cleared on success. The promise stays cached for
        // the life of the page, so this can only ever create one account per
        // load however many times it's called. Clearing it here instead
        // reopened the exact hole the guard exists to close: React's
        // StrictMode remounts ProtectedRoute, and if the first request had
        // already finished, the second mount sailed past a null flag and
        // created a second guest account.
        return data.user
      } catch {
        // Only a failure clears it, so the visitor gets another chance rather
        // than being stuck behind a permanently rejected promise.
        guestSessionInFlight = null
        setPlanLoading(false)
        return null
      }
    })()

    return guestSessionInFlight
  }

  // On app load — restore the session from storage, and renew it.
  //
  // Tokens last 7 days. Swapping the saved one for a fresh token on every load
  // is what makes "still signed in" true for anyone who opens JAI at least
  // once a week, rather than being logged out on a rolling weekly basis.
  useEffect(() => {
    const savedToken = readSync('jai_token')
    const savedUser = readSync('jai_user')

    if (!savedToken || !savedUser) {
      setPlanLoading(false) // no session to restore — nothing to wait for
      setLoading(false)
      return
    }

    let parsedUser
    try {
      parsedUser = JSON.parse(savedUser)
    } catch {
      // Corrupted storage — clear it and start over as a new visitor.
      clearSession()
      setPlanLoading(false)
      setLoading(false)
      return
    }

    // Take ownership of the dead-session decision for the duration of this
    // check. Several requests go out together below holding the same saved
    // token, so a dead one produces a burst of 401s — and the api.js
    // interceptor answers every 401 by redirecting to /login, which is the
    // wrong answer for a guest and would win the race against the recovery
    // just underneath. Released once this has decided. See authRedirect.
    authRedirect.suspended = true

    // Show the saved session immediately so the app renders without waiting on
    // the network, then renew in the background.
    setToken(savedToken)
    setUser(parsedUser)
    // Most visits are returning users, who never pass back through login() —
    // without this, the bulk of real traffic would be recorded as anonymous.
    identifyUser(parsedUser)
    refreshPlan()
    setLoading(false)

    authApi.refreshToken(savedToken)
      .then(({ data }) => {
        if (data?.token) {
          writeSync('jai_token', data.token)
          setToken(data.token)
        }
      })
      .catch(async (err) => {
        // Only an outright rejection means the session is dead. A network
        // blip leaves the existing token alone — it may well still be valid,
        // and signing someone out over a dropped request would be worse than
        // letting them carry on until a real 401 arrives.
        if (err.response?.status !== 401) return

        clearSession()
        // A guest can't sign in — there's no password and no email — so the
        // only sensible recovery is a fresh guest session. Their old data is
        // unreachable at that point, which is the deal guests accept; it's why
        // the app keeps nudging them to claim the account.
        //
        // A registered user is flagged instead, which ProtectedRoute turns
        // into a redirect to /login — the same place the interceptor would
        // have sent them, just decided in one place rather than by whichever
        // 401 happened to land first.
        if (parsedUser?.isGuest) await startGuestSession()
        else setSessionExpired(true)
      })
      .finally(() => {
        // Awaited above, so by now either a new session is stored or there
        // genuinely isn't one. Any 401 after this really is a session dying
        // mid-use, which is exactly what the interceptor is for.
        authRedirect.suspended = false
      })
    // Mount only, deliberately. This restores the saved session exactly once
    // per page load; re-running it because startGuestSession got a new
    // identity on a re-render would re-issue the token exchange on every
    // render. Everything it closes over is either a setState function or
    // module-level state, so the first-render closure stays correct.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const login = async (email, password) => {
    const response = await authApi.login(email, password)
    const { token: newToken, user: newUser } = response.data

    storeSession(newToken, newUser)
    // identify before capture, so the event is attributed to the account
    // rather than to the anonymous visitor who existed a moment ago.
    identifyUser(newUser)
    trackEvent('login')
    refreshPlan()
    hapticSignInSuccess()

    return response.data
  }

  // details = { firstName, lastName, username, email, password }
  //
  // Signing up while browsing as a guest CLAIMS that guest account rather than
  // creating a second one, so the league they connected and the notes they
  // saved a minute ago are still there afterwards. Same fields either way —
  // the person filling in the form can't tell the difference, which is the
  // point.
  const signup = async (details) => {
    const isClaimingGuest = !!user?.isGuest
    const response = isClaimingGuest
      ? await authApi.claimAccount(details)
      : await authApi.signup(details)
    const { token: newToken, user: newUser } = response.data

    storeSession(newToken, newUser)
    setPlan('starter') // brand new account — always starts on Starter
    setPlanInterval(null)
    setNeedsLeagueSelection(false)
    // Which of the two paths a signup came through is worth knowing: a guest
    // who converts had already tried the product, and that's a different
    // funnel from someone signing up off the landing page cold.
    //
    // Claiming a guest account keeps PostHog's existing distinct_id and
    // attaches it to the new account, so everything the person did while
    // browsing as a guest stays on the same timeline as what they do after
    // signing up. Calling reset() here instead would split one person into two.
    identifyUser(newUser)
    trackEvent('sign_up', { method: isClaimingGuest ? 'guest_claim' : 'direct' })
    // Still worth a refresh: a brand-new account may have Founding Manager
    // perks waiting if this email earned referrals on the waitlist.
    refreshPlan()

    return response.data
  }

  /**
   * Update the cached copy of the user after Settings changes something.
   *
   * The sidebar and account menu read the name and email straight out of this
   * context, so without it a renamed user keeps seeing their old name until
   * they next log in. Merges rather than replaces, since the account endpoints
   * return the account fields but not the plan-related ones.
   */
  const updateUser = (fields) => {
    setUser(prev => {
      const merged = { ...(prev || {}), ...fields }
      writeSync('jai_user', JSON.stringify(merged))
      return merged
    })
  }

  /**
   * Swap in a token the server just handed back.
   *
   * Changing a password and "log out everywhere" both invalidate every token
   * issued before that moment — including the one this browser is holding. The
   * server returns a replacement so the person isn't logged out of the very
   * screen they clicked the button on; this stores it.
   */
  const replaceToken = (newToken) => {
    if (!newToken) return
    writeSync('jai_token', newToken)
    setToken(newToken)
  }

  const logout = async () => {
    // Before clearing the session — unregistering needs the still-valid
    // auth token to authenticate the DELETE call. A no-op on web and if no
    // device was ever registered (see teardownPushNotifications).
    await teardownPushNotifications()
    try {
      await authApi.logout()
    } catch {
      // Continue even if server logout fails
    }
    clearSession()
    // Stop attributing this browser to the account that just left. On a shared
    // laptop the next person's activity would otherwise land on their profile.
    resetUser()
    setPlan('starter')
    setPlanInterval(null)
    setScheduledChange(null)
    setNeedsLeagueSelection(false)
    setReferralUnlocks([])
    setFoundingManager(false)
  }

  return (
    <AuthContext.Provider value={{
      user, token, loading, plan, planInterval, scheduledChange, planLoading, needsLeagueSelection,
      // HiddenRoute reads this; it was set in state but never exposed, so every
      // pre-launch tool page crashed on `undefined.includes` (11 Oct 2026).
      referralUnlocks,
      isGuest: !!user?.isGuest,
      sessionExpired,
      refreshPlan, login, logout, signup, startGuestSession, updateUser, replaceToken,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
